import {
  WorkerContract,
  WorkerContractsResponseDataItem,
} from '@kaa/api/providers';
import { httpTo } from '@kaa/api/providers/utilities';
import { useAsyncCallback } from '@kaa/common/utils';
import { i18nKeys } from '@kaa/i18n/providers/keys';
import {
  AlertType,
  SwActionGroup,
  SwButton,
  SwForm,
  SwFormGrid,
  SwFormSubmitMessage,
  SwTitle,
  toggleModalById,
} from '@kaa/ui-flanders/components';
import { Formik, FormikActions } from 'formik';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modals } from '../../../../../constants';
import { dataTest } from '../../../../../datatest/keys';
import { useApi } from '../../../../../utils';
import {
  createValidatorWithServerErrorHandled,
  handleApiError,
} from '../../../../../utils/validation';
import { ContractForm } from './ContractForm';
import { ContractModalProps } from './ContractForm.types';
import { getDefaultContractValidation } from './ContractForm.utils';

export const AddContractModal = ({
  providerId,
  workerId,
  onSuccess,
  setConfirmCloseModal,
}: ContractModalProps) => {
  const { t } = useTranslation();
  const { providers } = useApi();

  const [{ value: submitedForm }, submit] = useAsyncCallback(
    async (
      formikData: WorkerContract,
      formikActions: FormikActions<WorkerContract>,
    ) => {
      const [error, response] = await httpTo(
        providers.createWorkerContract(
          Number(providerId),
          Number(workerId),
          formikData,
        ),
      );

      return { error, response, formikData, formikActions };
    },
    [providers, providerId],
  );

  useEffect(() => {
    if (submitedForm) {
      const { error, response, formikData, formikActions } = submitedForm;
      const { setSubmitting, setStatus } = formikActions;
      setSubmitting(false);

      if (handleApiError(error, formikActions)) {
        return;
      }

      if (!response) {
        setStatus({
          msg: t(i18nKeys.contactPersons.modal.add.errorMessage),
          type: AlertType.ERROR,
        });
        return;
      }

      toggleModalById(Modals.WORKERS_ADD_CONTRACT_MODAL);

      onSuccess({
        ...formikData,
        id: response.data.data.id,
      } as WorkerContractsResponseDataItem);
    }
  }, [submitedForm]);

  const validate = createValidatorWithServerErrorHandled(
    getDefaultContractValidation(),
  );

  return (
    <>
      <SwTitle tagName="h2">
        {t(i18nKeys.contract.modal.addContract.title)}
      </SwTitle>
      <Formik
        initialValues={{}}
        onSubmit={submit}
        validate={validate}
        render={({ handleSubmit, dirty, isSubmitting }) => {
          setConfirmCloseModal(dirty);
          return (
            <SwForm onSubmit={handleSubmit}>
              <SwFormGrid modStacked>
                <ContractForm />
                <SwActionGroup style={{ marginTop: '3rem' }}>
                  <SwButton
                    type="submit"
                    data-testid={dataTest.workers.details.contract.addContract}
                    modLoading={isSubmitting}
                    modLarge
                    modDisabled={!dirty}
                  >
                    {t(i18nKeys.worker.details.contract.cta.add)}
                  </SwButton>
                </SwActionGroup>
                <SwFormSubmitMessage />
              </SwFormGrid>
            </SwForm>
          );
        }}
      />
    </>
  );
};
